import { createReducer } from '@reduxjs/toolkit';
import { IMessageModel } from '../../../model/communication/Message';
import { ThingTypes } from '../../../model/ryve/Thing';
import {
  initBotChatSession,
  hydrateBotChatFromSaved,
  addBotChatUserMessage,
  setBotChatSessionId,
  botChatResponseChunk,
  botChatResponseEnd,
  setBotChatWorking,
  saveBotChatRequest,
  saveBotChatSuccess,
  saveBotChatFailed,
  discardBotChat,
  resetBotChat,
  deleteBotChatRequest,
  deleteBotChatSuccess,
  deleteBotChatFailed,
  createComplaintRequest,
  createComplaintSuccess,
  createComplaintFailed,
} from '../../actions/botChat';

export const BOT_CREATED_BY = 'medtec-online-bot';
export const BOT_FULL_NAME = 'Medtec Online-Bot';
export const BOT_ORGANISATION = 'MTO Medtec Online GmbH';

export function buildBotMessage(content: string, id?: string): IMessageModel {
  const now = Date.now();
  return {
    id: id ?? 'bot-' + now + '-' + Math.random().toString(36).slice(2, 8),
    parent: '',
    content,
    emotes: {},
    deleted: false,
    createdBy: BOT_CREATED_BY,
    createdAt: now,
    modifiedAt: now,
    modifiedBy: BOT_CREATED_BY,
    createdByResolved: {
      _id: BOT_CREATED_BY,
      type: ThingTypes.Profile,
      content: {
        userId: BOT_CREATED_BY,
        fullName: BOT_FULL_NAME,
        organisation: BOT_ORGANISATION,
        isAdmin: false,
        chatAllowed: false,
        externalLinksAllowed: false,
      },
    } as any,
  } as IMessageModel;
}

export interface IBotChatState {
  active: boolean;
  sessionId?: string;
  /**
   * When set, this slice is bound to an already-saved bot chat in MongoDB.
   * Each new turn appended to `messages` should also be persisted server-side
   * via the saga's append-to-saved-chat branch.
   */
  savedCommunicationId?: string;
  messages: IMessageModel[];
  currentBotResponse: string;
  currentBotResponseDict: Record<string, string>;
  botWorking: boolean;
  saving: boolean;
  /** True while a delete request is in flight for the saved bot chat. */
  deleting: boolean;
  /** True while a complaint chat is being opened with the operator. */
  complaintCreating: boolean;
  /** Set once the complaint chat exists, so the view can navigate to it. */
  createdComplaint?: { id: string; alias?: string };
  complaintFailed?: boolean;
}

const initialState: IBotChatState = {
  active: false,
  sessionId: undefined,
  savedCommunicationId: undefined,
  messages: [],
  currentBotResponse: '',
  currentBotResponseDict: {},
  botWorking: false,
  saving: false,
  deleting: false,
  complaintCreating: false,
};

const botChat = createReducer(initialState, builder =>
  builder
    .addCase(initBotChatSession, () => {
      return { ...initialState, active: true };
    })
    .addCase(hydrateBotChatFromSaved, (_state, action) => {
      return {
        ...initialState,
        active: true,
        savedCommunicationId: action.payload.communicationId,
        sessionId: action.payload.sessionId,
        messages: Array.isArray(action.payload.messages) ? action.payload.messages : [],
      };
    })
    .addCase(createComplaintRequest, state => {
      return { ...state, complaintCreating: true, createdComplaint: undefined, complaintFailed: false };
    })
    .addCase(createComplaintSuccess, (state, action) => {
      return { ...state, complaintCreating: false, createdComplaint: { id: action.payload.id, alias: action.payload.alias } };
    })
    .addCase(createComplaintFailed, state => {
      return { ...state, complaintCreating: false, complaintFailed: true };
    })
    .addCase(addBotChatUserMessage, (state, action) => {
      return {
        ...state,
        messages: [...state.messages, action.payload.message],
      };
    })
    .addCase(setBotChatSessionId, (state, action) => {
      return { ...state, sessionId: action.payload.sessionId };
    })
    .addCase(botChatResponseChunk, (state, action) => {
      const chunkLength = Object.keys(state.currentBotResponseDict).length + 1;
      const unsortedChunks = { ...state.currentBotResponseDict, [action.payload.timestamp]: action.payload.chunk };
      if (chunkLength % 10 !== 0) {
        return {
          ...state,
          currentBotResponseDict: unsortedChunks,
        };
      }
      const sortedKeys = Object.keys(unsortedChunks).sort();
      const sortedChunks = sortedKeys.reduce<Record<string, string>>((acc, key) => {
        acc[key] = unsortedChunks[key];
        return acc;
      }, {});
      return {
        ...state,
        currentBotResponse: Object.values(sortedChunks).join(''),
        currentBotResponseDict: sortedChunks,
      };
    })
    .addCase(botChatResponseEnd, (state, action) => {
      const botMessage = buildBotMessage(action.payload.response);
      return {
        ...state,
        messages: [...state.messages, botMessage],
        currentBotResponse: '',
        currentBotResponseDict: {},
        botWorking: false,
        sessionId: action.payload.sessionId || state.sessionId,
      };
    })
    .addCase(setBotChatWorking, (state, action) => {
      return { ...state, botWorking: action.payload.working };
    })
    .addCase(saveBotChatRequest, (state) => {
      return { ...state, saving: true };
    })
    .addCase(saveBotChatSuccess, () => {
      return { ...initialState };
    })
    .addCase(saveBotChatFailed, (state) => {
      return { ...state, saving: false };
    })
    .addCase(deleteBotChatRequest, (state) => {
      return { ...state, deleting: true };
    })
    .addCase(deleteBotChatSuccess, () => {
      return { ...initialState };
    })
    .addCase(deleteBotChatFailed, (state) => {
      return { ...state, deleting: false };
    })
    .addCase(discardBotChat, () => {
      return { ...initialState };
    })
    .addCase(resetBotChat, () => {
      return { ...initialState };
    })
);

export default botChat;
